import { useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Search, Disc3, Music, Video, Play, Radio, Filter } from "lucide-react";
import { api, mediaUrl } from "@/lib/api";
import { StatusPill, EmptyState } from "@/components/dashboard/ui";
import ReleaseDetailDialog from "@/components/releases/ReleaseDetailDialog";

const TYPES = ["All", "Single", "EP", "Album", "Music Video"];
const STATUSES = ["All", "Draft", "In Review", "Live", "Rejected"];

export default function LabelCatalog() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [type, setType] = useState("All");
  const [status, setStatus] = useState("All");
  const [detailId, setDetailId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/label/catalog");
      setRows(data);
    } catch (e) {
      toast.error("Failed to load catalog");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter((r) => {
      if (type !== "All" && r.type !== type) return false;
      if (status !== "All" && r.status !== status) return false;
      if (!q) return true;
      return [r.title, r.artist_name, r.display_artist, r.upc, r.catalog_number]
        .filter(Boolean).some((f) => String(f).toLowerCase().includes(q));
    });
  }, [rows, query, type, status]);

  const stats = useMemo(() => {
    const live = rows.filter((r) => r.status === "Live").length;
    const total_streams = rows.reduce((s, r) => s + (r.streams_30d ?? 0), 0);
    return { total: rows.length, live, streams: total_streams };
  }, [rows]);

  return (
    <div className="space-y-6" data-testid="label-catalog">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-display text-2xl tracking-tight text-white">Catalog</h2>
          <p className="mt-1.5 text-slate-400">Every release across your roster — click a card to preview or download.</p>
        </div>
        <div className="flex items-center gap-3 text-xs text-slate-400">
          <span className="rounded-full bg-white/5 border border-white/10 px-3 py-1"><span className="text-white tabular-nums">{stats.total}</span> total</span>
          <span className="rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-300 px-3 py-1"><span className="tabular-nums">{stats.live}</span> live</span>
          <span className="rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-300 px-3 py-1"><span className="tabular-nums">{stats.streams.toLocaleString()}</span> streams / 30d</span>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            data-testid="catalog-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search title, artist, UPC, catalog #…"
            className="w-full bg-white/5 border border-white/10 rounded-xl pl-9 pr-3 py-2.5 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
          />
        </div>
        <div className="flex items-center gap-1 border border-white/10 rounded-xl bg-white/[0.02] p-1 flex-wrap">
          {TYPES.map((t) => (
            <button
              key={t}
              data-testid={`catalog-type-${t.replace(/ /g, "-").toLowerCase()}`}
              onClick={() => setType(t)}
              className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg transition-colors ${type === t ? "bg-white text-black" : "text-slate-300 hover:text-white"}`}
            >
              {t === "Music Video" ? <Video size={12} /> : t !== "All" ? <Music size={12} /> : <Filter size={12} />}
              {t}
            </button>
          ))}
        </div>
      </div>

      <div className="flex items-center gap-2 border-b border-white/10 overflow-x-auto -mx-1 px-1">
        {STATUSES.map((s) => (
          <button
            key={s}
            data-testid={`catalog-status-${s.replace(/ /g, "-").toLowerCase()}`}
            onClick={() => setStatus(s)}
            className={`relative px-4 py-2.5 text-sm transition-colors whitespace-nowrap ${status === s ? "text-white" : "text-slate-400 hover:text-white"}`}
          >
            {s}
            {status === s && (
              <motion.span layoutId="catalog-status-underline" className="absolute left-0 right-0 -bottom-px h-[2px] bg-white" transition={{ type: "spring", stiffness: 500, damping: 32 }} />
            )}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5" data-testid="catalog-skeleton">
          {[0,1,2,3,4,5,6,7].map((i) => (
            <div key={i} className="rounded-2xl border border-white/10 overflow-hidden">
              <div className="aspect-square nz-shimmer bg-white/[0.02]" />
              <div className="p-4 space-y-2">
                <div className="h-4 w-3/4 rounded-md bg-white/5 nz-shimmer" />
                <div className="h-3 w-1/2 rounded-md bg-white/5 nz-shimmer" />
              </div>
            </div>
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState
          title={rows.length ? "No results match those filters" : "No releases yet"}
          desc={rows.length ? "Try clearing filters or searching for another title." : "Once your artists create releases, they'll show up here."}
        />
      ) : (
        <motion.div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5"
          initial="hidden"
          animate="show"
          variants={{ hidden: {}, show: { transition: { staggerChildren: 0.05 } } }}
        >
          <AnimatePresence>
            {filtered.map((r) => {
              const isVideo = r.type === "Music Video";
              return (
                <motion.button
                  type="button"
                  key={r.release_id}
                  data-testid={`catalog-card-${r.release_id}`}
                  onClick={() => setDetailId(r.release_id)}
                  variants={{
                    hidden: { opacity: 0, y: 14 },
                    show:   { opacity: 1, y: 0, transition: { type: "spring", stiffness: 240, damping: 26 } },
                  }}
                  exit={{ opacity: 0, y: -10, transition: { duration: 0.18 } }}
                  whileHover={{ y: -4 }}
                  className="text-left rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden group hover:border-white/20 hover:shadow-[0_20px_60px_-20px_rgba(56,127,255,0.35)] transition-[border-color,box-shadow] duration-300"
                >
                  <div className="relative aspect-square overflow-hidden bg-gradient-to-br from-blue-900/40 to-[#040a1a] flex items-center justify-center">
                    {r.cover_url ? (
                      <img
                        src={mediaUrl(r.cover_url)}
                        alt={r.title}
                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-[1.06]"
                      />
                    ) : (
                      <Disc3 size={48} className="text-blue-400/40 transition-transform duration-500 group-hover:rotate-45" />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/0 to-black/0 opacity-70 group-hover:opacity-90 transition-opacity" />
                    <div className="absolute top-3 left-3 flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-white/85 bg-black/40 backdrop-blur-sm border border-white/10 rounded-full px-2 py-1">
                      {isVideo ? <><Video size={10} /> Video</> : <><Music size={10} /> {r.type}</>}
                    </div>
                    <div className="absolute top-3 right-3"><StatusPill status={r.status} /></div>
                    <div className="absolute bottom-3 right-3 h-11 w-11 rounded-full bg-white text-black flex items-center justify-center opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-300 shadow-lg">
                      <Play size={16} className="ml-0.5" />
                    </div>
                  </div>
                  <div className="p-4">
                    <div className="text-white text-sm font-medium truncate">{r.title}</div>
                    <div className="text-[11px] text-slate-500 truncate mt-0.5">{r.display_artist || r.artist_name}</div>
                    <div className="mt-3 flex items-center justify-between text-[11px] text-slate-400">
                      <span className="flex items-center gap-1"><Radio size={11} /> {(r.streams_30d ?? 0).toLocaleString()}</span>
                      <span className="text-slate-500">{r.upc ? `UPC ${r.upc.slice(-6)}` : ""}</span>
                    </div>
                  </div>
                </motion.button>
              );
            })}
          </AnimatePresence>
        </motion.div>
      )}

      <ReleaseDetailDialog
        open={!!detailId}
        onOpenChange={(o) => { if (!o) setDetailId(null); }}
        releaseId={detailId}
        isOwner
        onChanged={load}
      />
    </div>
  );
}
